import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Collapse,
    Card,
    Container,
    Grid,
    Group,
    Loader,
    Paper,
    Select,
    MultiSelect,
    SimpleGrid,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from "@mantine/core";
import * as XLSX from "xlsx";   

import { getAuthHeaders } from "../../../api/authHelper";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";
import { useNavigate } from "react-router-dom";

// ---------------------------------------------------------
// TYPES
// ---------------------------------------------------------

interface FormPracticeDto {
    practiceId?: number;
    country?: string;
    practiceDate?: string;

    practiceTime?: number;
    practiceLocation?: number;

    numberOfCourts?: number;
    numberOfPlayers?: number;
    numberOfMinuts?: number;

    createdDate?: string;
    eventId?: number;
    userId?: number;
}

interface OptionDto {
    value: number;
    text: string;
    eventid?: number;
}

interface PracticeDateDto {
    value: number;
    date: string;
    eventId: number;
}

interface EventDto {
    eventID: number;
    eventName: string;
    startTime?: string;
    endTime?: string;
}

interface GroupedPractice {
    key: string;

    date: string;

    timeValue?: number;
    timeText: string;

    locationValue?: number;
    locationText: string;

    bookings: FormPracticeDto[];

    totalCourts: number;
    totalPlayers: number;
}

interface CountrySummary {
    country: string;
    bookings: number;
    courts: number;
    players: number;
}

interface LocationSummary {
    locationValue?: number;
    location: string;
    bookings: number;
    courts: number;
    players: number;
}

// ---------------------------------------------------------
// HELPERS
// ---------------------------------------------------------

const normalizeDate = (date?: string) => {
    if (!date) return "";

    if (date.includes("T")) {
        return date.split("T")[0];
    }

    return date;
};

const formatDate = (date?: string) => {
    const normalized = normalizeDate(date);

    if (!normalized) {
        return "—";
    }

    const value = new Date(`${normalized}T00:00:00`);

    if (Number.isNaN(value.getTime())) {
        return normalized;
    }

    return value.toLocaleDateString("da-DK", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

// ---------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------

const FormPracticeAdminPage: React.FC = () => {
    const apiBase = process.env.REACT_APP_API_URL;

    // ---------------------------------------------------------
    // URLS
    // ---------------------------------------------------------

    const PRACTICE_URL = `${apiBase}/api/FormPractice`;

    const PRACTICE_TIME_URL =
        `${apiBase}/api/Option_PracticeTime`;

    const PRACTICE_MINUTES_URL =
        `${apiBase}/api/Option_PracticeMinuts`;

    const PRACTICE_LOCATION_URL =
        `${apiBase}/api/Option_PracticeLocation`;

    const PRACTICE_DATE_URL =
        `${apiBase}/api/practice-planner/dates`;

    const EVENTS_URL =
        `${apiBase}/api/Events`;

    // ---------------------------------------------------------
    // STATE
    // ---------------------------------------------------------

    const [events, setEvents] = useState<EventDto[]>([]);

    const [selectedEventId, setSelectedEventId] =
        useState<string | null>(null);

    const [practices, setPractices] =
        useState<FormPracticeDto[]>([]);

    const [practiceTimes, setPracticeTimes] =
        useState<OptionDto[]>([]);

    const [practiceMinutes, setPracticeMinutes] =
        useState<OptionDto[]>([]);

    const [practiceLocations, setPracticeLocations] =
        useState<OptionDto[]>([]);

    const [loadingEvents, setLoadingEvents] =
        useState(false);

    const [loadingPractices, setLoadingPractices] =
        useState(false);

    const [newLocationName, setNewLocationName] = useState("");

    const [editingLocationId, setEditingLocationId] =
        useState<number | null>(null);

    const [editingLocationName, setEditingLocationName] =
        useState("");

    const [savingLocation, setSavingLocation] =
        useState(false);

    const [newPracticeTime, setNewPracticeTime] =
        useState("");

    const [selectedPracticeTimes, setSelectedPracticeTimes] =
        useState<string[]>([]);

    const [editingPracticeTimeId, setEditingPracticeTimeId] =
        useState<number | null>(null);

    const [editingPracticeTimeText, setEditingPracticeTimeText] =
        useState("");

    const [newPracticeMinutes, setNewPracticeMinutes] =
        useState("");

    const [editingPracticeMinutesId, setEditingPracticeMinutesId] =
        useState<number | null>(null);

    const [editingPracticeMinutesText, setEditingPracticeMinutesText] =
        useState("");

    const [savingPracticeOptions, setSavingPracticeOptions] =
        useState(false);

    const [locationsOpen, setLocationsOpen] =
        useState(false);

    const [timesOpen, setTimesOpen] =
        useState(false);

    const [minutesOpen, setMinutesOpen] =
        useState(false);

    const [practiceDates, setPracticeDates] =
        useState<PracticeDateDto[]>([]);

    const [datesOpen, setDatesOpen] =
        useState(false);

    const [newPracticeDate, setNewPracticeDate] =
        useState("");

    const [editingPracticeDateId, setEditingPracticeDateId] =
        useState<number | null>(null);

    const [editingPracticeDate, setEditingPracticeDate] =
        useState("");

    const [error, setError] =
        useState<string | null>(null);

    const navigate = useNavigate();

    // ---------------------------------------------------------
    // FILTERS
    // ---------------------------------------------------------

    const [countryFilter, setCountryFilter] =
        useState("");

    const [dateFilter, setDateFilter] =
        useState<string | null>(null);

    const [timeFilter, setTimeFilter] =
        useState<string | null>(null);

    const [locationFilter, setLocationFilter] =
        useState<string | null>(null);

    // ---------------------------------------------------------
    // OPTION HELPERS
    // ---------------------------------------------------------

    const getPracticeTimeText = (value?: number) => {
        if (value === undefined || value === null) {
            return "—";
        }

        return (
            practiceTimes.find(
                (x) => x.value === value
            )?.text ?? String(value)
        );
    };

    const getPracticeMinutesText = (value?: number) => {
        if (value === undefined || value === null) {
            return "—";
        }

        return (
            practiceMinutes.find(
                (x) => x.value === value
            )?.text ?? `${value} min.`
        );
    };

    const getPracticeLocationText = (
        value?: number
    ) => {
        if (value === undefined || value === null) {
            return "—";
        }

        return (
            practiceLocations.find(
                (x) => x.value === value
            )?.text ?? String(value)
        );
    };

    const createPracticeTime = async (
        time: string
    ) => {
        if (!selectedEventId) return;

        const text = time.trim();

        if (!text) {
            setError("Angiv et tidspunkt.");
            return;
        }

        try {
            setSavingPracticeOptions(true);
            setError(null);

            const res = await fetch(
                `${apiBase}/api/practice-planner/times`,
                {
                    method: "POST",
                    headers:
                        getAuthHeaders({
                            "Content-Type": "application/json",
                        }) as Record<string, string>,
                    body: JSON.stringify({
                        text,
                        eventId: Number(selectedEventId),
                    }),
                }
            );

            if (!res.ok) {
                const message = await res.text();

                throw new Error(
                    message ||
                    "Kunne ikke oprette tidspunktet."
                );
            }

            await fetchPracticeTimes(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.message ||
                "Kunne ikke oprette tidspunktet."
            );
        } finally {
            setSavingPracticeOptions(false);
        }
    };

    const updatePracticeTime = async () => {
        if (
            !editingPracticeTimeId ||
            !selectedEventId
        ) {
            return;
        }

        const text =
            editingPracticeTimeText.trim();

        if (!text) {
            setError("Angiv et tidspunkt.");
            return;
        }

        try {
            setSavingPracticeOptions(true);
            setError(null);

            const res = await fetch(
                `${apiBase}/api/practice-planner/times/${editingPracticeTimeId}`,
                {
                    method: "PUT",
                    headers:
                        getAuthHeaders({
                            "Content-Type": "application/json",
                        }) as Record<string, string>,
                    body: JSON.stringify({
                        text,
                        eventId: Number(selectedEventId),
                    }),
                }
            );

            if (!res.ok) {
                const message = await res.text();
                throw new Error(
                    message || "Kunne ikke opdatere tidspunktet."
                );
            }

            setEditingPracticeTimeId(null);
            setEditingPracticeTimeText("");

            await fetchPracticeTimes(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);
            setError(
                err?.message ||
                "Kunne ikke opdatere tidspunktet."
            );
        } finally {
            setSavingPracticeOptions(false);
        }
    };

    const deletePracticeTime = async (
        value: number
    ) => {
        if (
            !window.confirm(
                "Er du sikker på, at du vil slette dette tidspunkt?"
            )
        ) {
            return;
        }

        try {
            setSavingPracticeOptions(true);
            setError(null);

            const res = await fetch(
                `${apiBase}/api/practice-planner/times/${value}`,
                {
                    method: "DELETE",
                    headers:
                        getAuthHeaders() as Record<
                            string,
                            string
                        >,
                }
            );

            if (!res.ok) {
                const message = await res.text();
                throw new Error(
                    message || "Kunne ikke slette tidspunktet."
                );
            }

            await fetchPracticeTimes(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);
            setError(
                err?.message ||
                "Kunne ikke slette tidspunktet."
            );
        } finally {
            setSavingPracticeOptions(false);
        }
    };

    const createPracticeMinutes = async () => {
        if (!selectedEventId) return;

        const text = newPracticeMinutes.trim();

        if (!text) {
            setError("Angiv en varighed.");
            return;
        }

        try {
            setSavingPracticeOptions(true);
            setError(null);

            const res = await fetch(
                `${apiBase}/api/practice-planner/minutes`,
                {
                    method: "POST",
                    headers:
                        getAuthHeaders({
                            "Content-Type": "application/json",
                        }) as Record<string, string>,
                    body: JSON.stringify({
                        text,
                        eventId: Number(selectedEventId),
                    }),
                }
            );

            if (!res.ok) {
                const message = await res.text();
                throw new Error(
                    message || "Kunne ikke oprette varigheden."
                );
            }

            setNewPracticeMinutes("");

            await fetchPracticeMinutes(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);
            setError(
                err?.message ||
                "Kunne ikke oprette varigheden."
            );
        } finally {
            setSavingPracticeOptions(false);
        }
    };

    const updatePracticeMinutes = async () => {
        if (
            !editingPracticeMinutesId ||
            !selectedEventId
        ) {
            return;
        }

        const text =
            editingPracticeMinutesText.trim();

        if (!text) {
            setError("Angiv en varighed.");
            return;
        }

        try {
            setSavingPracticeOptions(true);
            setError(null);

            const res = await fetch(
                `${apiBase}/api/practice-planner/minutes/${editingPracticeMinutesId}`,
                {
                    method: "PUT",
                    headers:
                        getAuthHeaders({
                            "Content-Type": "application/json",
                        }) as Record<string, string>,
                    body: JSON.stringify({
                        text,
                        eventId: Number(selectedEventId),
                    }),
                }
            );

            if (!res.ok) {
                const message = await res.text();
                throw new Error(
                    message || "Kunne ikke opdatere varigheden."
                );
            }

            setEditingPracticeMinutesId(null);
            setEditingPracticeMinutesText("");

            await fetchPracticeMinutes(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);
            setError(
                err?.message ||
                "Kunne ikke opdatere varigheden."
            );
        } finally {
            setSavingPracticeOptions(false);
        }
    };

    const deletePracticeMinutes = async (
        value: number
    ) => {
        if (
            !window.confirm(
                "Er du sikker på, at du vil slette denne varighed?"
            )
        ) {
            return;
        }

        try {
            setSavingPracticeOptions(true);
            setError(null);

            const res = await fetch(
                `${apiBase}/api/practice-planner/minutes/${value}`,
                {
                    method: "DELETE",
                    headers:
                        getAuthHeaders() as Record<
                            string,
                            string
                        >,
                }
            );

            if (!res.ok) {
                const message = await res.text();
                throw new Error(
                    message || "Kunne ikke slette varigheden."
                );
            }

            await fetchPracticeMinutes(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);
            setError(
                err?.message ||
                "Kunne ikke slette varigheden."
            );
        } finally {
            setSavingPracticeOptions(false);
        }
    };

    // ---------------------------------------------------------
    // LOAD EVENTS
    // ---------------------------------------------------------

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoadingEvents(true);
            setError(null);

            const res = await fetch(EVENTS_URL, {
                headers:
                    getAuthHeaders() as Record<
                        string,
                        string
                    >,
            });

            if (!res.ok) {
                throw new Error(
                    `Kunne ikke hente events (${res.status})`
                );
            }

            const data = await res.json();

            const eventList: EventDto[] =
                Array.isArray(data)
                    ? data
                    : data.items ??
                    data.events ??
                    [];

            setEvents(eventList);

            if (eventList.length > 0) {
                setSelectedEventId(
                    String(eventList[0].eventID)
                );
            }
        } catch (err) {
            console.error(err);

            setError(
                "Kunne ikke hente events."
            );
        } finally {
            setLoadingEvents(false);
        }
    };

    // ---------------------------------------------------------
    // EVENT CHANGE
    // ---------------------------------------------------------

    useEffect(() => {
        if (!selectedEventId) {
            setPractices([]);
            setPracticeTimes([]);
            setPracticeMinutes([]);
            setPracticeLocations([]);
            setPracticeDates([]);

            return;
        }

        const eventId =
            Number(selectedEventId);

        resetFilters();

        loadEventData(eventId);
    }, [selectedEventId]);

    // ---------------------------------------------------------
    // LOAD ALL EVENT DATA
    // ---------------------------------------------------------

    const loadEventData = async (
        eventId: number
    ) => {
        try {
            setLoadingPractices(true);
            setError(null);

            await Promise.all([
                fetchPractices(eventId),
                fetchPracticeTimes(eventId),
                fetchPracticeMinutes(eventId),
                fetchPracticeLocations(eventId),
                fetchPracticeDates(eventId),
            ]);
        } catch (err) {
            console.error(err);

            setError(
                "Der opstod en fejl ved indlæsning af træningsdata."
            );
        } finally {
            setLoadingPractices(false);
        }
    };

    // ---------------------------------------------------------
    // FETCH PRACTICES
    // ---------------------------------------------------------

    const fetchPractices = async (
        eventId: number
    ) => {
        const res = await fetch(
            `${PRACTICE_URL}/admin?eventId=${eventId}`,
            {
                headers:
                    getAuthHeaders() as Record<
                        string,
                        string
                    >,
            }
        );

        if (!res.ok) {
            throw new Error(
                `Kunne ikke hente træningsbookinger (${res.status})`
            );
        }

        const data: FormPracticeDto[] =
            await res.json();

        setPractices(
            Array.isArray(data) ? data : []
        );
    };

    // ---------------------------------------------------------
    // FETCH TIMES
    // ---------------------------------------------------------

    const fetchPracticeTimes = async (
        eventId: number
    ) => {
        const res = await fetch(
            `${PRACTICE_TIME_URL}?eventId=${eventId}`,
            {
                headers:
                    getAuthHeaders() as Record<
                        string,
                        string
                    >,
            }
        );

        if (!res.ok) {
            throw new Error(
                `Kunne ikke hente træningstider (${res.status})`
            );
        }

        const data = await res.json();

        setPracticeTimes(
            Array.isArray(data) ? data : []
        );
    };

    // ---------------------------------------------------------
    // FETCH MINUTES
    // ---------------------------------------------------------

    const fetchPracticeMinutes = async (
        eventId: number
    ) => {
        const res = await fetch(
            `${PRACTICE_MINUTES_URL}?eventId=${eventId}`,
            {
                headers:
                    getAuthHeaders() as Record<
                        string,
                        string
                    >,
            }
        );

        if (!res.ok) {
            throw new Error(
                `Kunne ikke hente varigheder (${res.status})`
            );
        }

        const data = await res.json();

        setPracticeMinutes(
            Array.isArray(data) ? data : []
        );
    };

    // ---------------------------------------------------------
    // FETCH LOCATIONS
    // ---------------------------------------------------------

    const fetchPracticeLocations = async (
        eventId: number
    ) => {
        const res = await fetch(
            `${apiBase}/api/practice-planner/locations/${eventId}`,
            {
                headers:
                    getAuthHeaders() as Record<
                        string,
                        string
                    >,
            }
        );

        if (!res.ok) {
            throw new Error(
                `Kunne ikke hente træningssteder (${res.status})`
            );
        }

        const data = await res.json();

        setPracticeLocations(
            Array.isArray(data) ? data : []
        );
    };

    // ---------------------------------------------------------
    // FETCH DATES
    // ---------------------------------------------------------

    const fetchPracticeDates = async (
        eventId: number
    ) => {
        const res = await fetch(
            `${PRACTICE_DATE_URL}/${eventId}`,
            {
                headers:
                    getAuthHeaders() as Record<
                        string,
                        string
                    >,
            }
        );

        if (!res.ok) {
            throw new Error(
                `Kunne ikke hente træningsdatoer (${res.status})`
            );
        }

        const data = await res.json();

        setPracticeDates(
            Array.isArray(data) ? data : []
        );
    };

    // ---------------------------------------------------------
    // CREATE DATE
    // ---------------------------------------------------------

    const createPracticeDate = async () => {
        if (!selectedEventId) {
            return;
        }

        if (!newPracticeDate) {
            setError("Vælg en dato.");
            return;
        }

        try {
            setSavingPracticeOptions(true);
            setError(null);

            const res = await fetch(
                PRACTICE_DATE_URL,
                {
                    method: "POST",

                    headers:
                        getAuthHeaders({
                            "Content-Type":
                                "application/json",
                        }) as Record<string, string>,

                    body: JSON.stringify({
                        date: newPracticeDate,
                        eventId: Number(selectedEventId),
                    }),
                }
            );

            if (!res.ok) {
                const message = await res.text();

                throw new Error(
                    message ||
                    `Kunne ikke oprette datoen (${res.status})`
                );
            }

            setNewPracticeDate("");

            await fetchPracticeDates(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.message ||
                "Kunne ikke oprette datoen."
            );
        } finally {
            setSavingPracticeOptions(false);
        }
    };

    // ---------------------------------------------------------
    // START EDIT DATE
    // ---------------------------------------------------------

    const startEditPracticeDate = (
        practiceDate: PracticeDateDto
    ) => {
        setEditingPracticeDateId(
            practiceDate.value
        );

        setEditingPracticeDate(
            normalizeDate(practiceDate.date)
        );
    };

    // ---------------------------------------------------------
    // CANCEL EDIT DATE
    // ---------------------------------------------------------

    const cancelEditPracticeDate = () => {
        setEditingPracticeDateId(null);
        setEditingPracticeDate("");
    };

    // ---------------------------------------------------------
    // UPDATE DATE
    // ---------------------------------------------------------

    const updatePracticeDate = async () => {
        if (
            !editingPracticeDateId ||
            !selectedEventId
        ) {
            return;
        }

        if (!editingPracticeDate) {
            setError("Vælg en dato.");
            return;
        }

        try {
            setSavingPracticeOptions(true);
            setError(null);

            const res = await fetch(
                `${PRACTICE_DATE_URL}/${editingPracticeDateId}`,
                {
                    method: "PUT",

                    headers:
                        getAuthHeaders({
                            "Content-Type":
                                "application/json",
                        }) as Record<string, string>,

                    body: JSON.stringify({
                        date: editingPracticeDate,
                        eventId: Number(selectedEventId),
                    }),
                }
            );

            if (!res.ok) {
                const message = await res.text();

                throw new Error(
                    message ||
                    `Kunne ikke opdatere datoen (${res.status})`
                );
            }

            cancelEditPracticeDate();

            await fetchPracticeDates(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.message ||
                "Kunne ikke opdatere datoen."
            );
        } finally {
            setSavingPracticeOptions(false);
        }
    };

    // ---------------------------------------------------------
    // DELETE DATE
    // ---------------------------------------------------------

    const deletePracticeDate = async (
        value: number
    ) => {
        if (
            !window.confirm(
                "Er du sikker på, at du vil slette denne dato?"
            )
        ) {
            return;
        }

        try {
            setSavingPracticeOptions(true);
            setError(null);

            const res = await fetch(
                `${PRACTICE_DATE_URL}/${value}`,
                {
                    method: "DELETE",

                    headers:
                        getAuthHeaders() as Record<
                            string,
                            string
                        >,
                }
            );

            if (!res.ok) {
                const message = await res.text();

                throw new Error(
                    message ||
                    `Kunne ikke slette datoen (${res.status})`
                );
            }

            await fetchPracticeDates(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.message ||
                "Kunne ikke slette datoen."
            );
        } finally {
            setSavingPracticeOptions(false);
        }
    };

    // ---------------------------------------------------------
    // CREATE LOCATION
    // ---------------------------------------------------------

    const createPracticeLocation = async () => {
        if (!selectedEventId) {
            return;
        }

        const name = newLocationName.trim();

        if (!name) {
            setError("Angiv et navn på træningsstedet.");
            return;
        }

        try {
            setSavingLocation(true);
            setError(null);

            const res = await fetch(
                `${apiBase}/api/practice-planner/locations`,
                {
                    method: "POST",

                    headers:
                        getAuthHeaders({
                            "Content-Type":
                                "application/json",
                        }) as Record<string, string>,

                    body: JSON.stringify({
                        eventId: Number(selectedEventId),
                        text: name,
                    }),
                }
            );

            if (!res.ok) {
                const message = await res.text();

                throw new Error(
                    message ||
                    `Kunne ikke oprette træningsstedet (${res.status})`
                );
            }

            setNewLocationName("");

            await fetchPracticeLocations(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.message ||
                "Kunne ikke oprette træningsstedet."
            );
        } finally {
            setSavingLocation(false);
        }
    };

    // ---------------------------------------------------------
    // START EDIT LOCATION
    // ---------------------------------------------------------

    const startEditLocation = (
        location: OptionDto
    ) => {
        setEditingLocationId(location.value);
        setEditingLocationName(location.text);
    };

    // ---------------------------------------------------------
    // CANCEL EDIT
    // ---------------------------------------------------------

    const cancelEditLocation = () => {
        setEditingLocationId(null);
        setEditingLocationName("");
    };

    // ---------------------------------------------------------
    // UPDATE LOCATION
    // ---------------------------------------------------------

    const updatePracticeLocation = async () => {
        if (
            !editingLocationId ||
            !selectedEventId
        ) {
            return;
        }

        const name =
            editingLocationName.trim();

        if (!name) {
            setError("Angiv et navn på træningsstedet.");
            return;
        }

        try {
            setSavingLocation(true);
            setError(null);

            const res = await fetch(
                `${apiBase}/api/practice-planner/locations/${editingLocationId}`,
                {
                    method: "PUT",

                    headers:
                        getAuthHeaders({
                            "Content-Type":
                                "application/json",
                        }) as Record<string, string>,

                    body: JSON.stringify({
                        text: name,
                        eventId: Number(selectedEventId),
                    }),
                }
            );

            if (!res.ok) {
                const message = await res.text();

                throw new Error(
                    message ||
                    `Kunne ikke opdatere træningsstedet (${res.status})`
                );
            }

            cancelEditLocation();

            await fetchPracticeLocations(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.message ||
                "Kunne ikke opdatere træningsstedet."
            );
        } finally {
            setSavingLocation(false);
        }
    };

    // ---------------------------------------------------------
    // DELETE LOCATION
    // ---------------------------------------------------------

    const deletePracticeLocation = async (
        locationId: number
    ) => {
        if (!selectedEventId) {
            return;
        }

        const confirmed =
            window.confirm(
                "Er du sikker på, at du vil slette dette træningssted?"
            );

        if (!confirmed) {
            return;
        }

        try {
            setSavingLocation(true);
            setError(null);

            const res = await fetch(
                `${apiBase}/api/practice-planner/locations/${locationId}`,
                {
                    method: "DELETE",

                    headers:
                        getAuthHeaders() as Record<
                            string,
                            string
                        >,
                }
            );

            if (!res.ok) {
                const message = await res.text();

                throw new Error(
                    message ||
                    `Kunne ikke slette træningsstedet (${res.status})`
                );
            }

            await fetchPracticeLocations(
                Number(selectedEventId)
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.message ||
                "Kunne ikke slette træningsstedet."
            );
        } finally {
            setSavingLocation(false);
        }
    };

    // ---------------------------------------------------------
    // REFRESH
    // ---------------------------------------------------------

    const refreshData = async () => {
        if (!selectedEventId) {
            return;
        }

        await loadEventData(
            Number(selectedEventId)
        );
    };

    // ---------------------------------------------------------
    // AVAILABLE DATES
    // ---------------------------------------------------------

    const availableDates = useMemo(() => {
        const dates = Array.from(
            new Set(
                practices
                    .map((x) =>
                        normalizeDate(
                            x.practiceDate
                        )
                    )
                    .filter(Boolean)
            )
        );

        return dates
            .sort()
            .map((date) => ({
                value: date,
                label: formatDate(date),
            }));
    }, [practices]);

    // ---------------------------------------------------------
    // FILTERED PRACTICES
    // ---------------------------------------------------------

    const filteredPractices =
        useMemo(() => {
            const countrySearch =
                countryFilter
                    .trim()
                    .toLowerCase();

            return practices
                .filter((practice) => {
                    // COUNTRY
                    if (
                        countrySearch &&
                        !practice.country
                            ?.toLowerCase()
                            .includes(
                                countrySearch
                            )
                    ) {
                        return false;
                    }

                    // DATE
                    if (
                        dateFilter &&
                        normalizeDate(
                            practice.practiceDate
                        ) !== dateFilter
                    ) {
                        return false;
                    }

                    // TIME
                    if (
                        timeFilter &&
                        String(practice.practiceTime).substring(0, 5) !== timeFilter
                    ) {
                        return false;
                    }

                    // LOCATION
                    if (
                        locationFilter &&
                        String(
                            practice.practiceLocation
                        ) !== locationFilter
                    ) {
                        return false;
                    }

                    return true;
                })
                .sort((a, b) => {
                    const dateA =
                        normalizeDate(
                            a.practiceDate
                        );

                    const dateB =
                        normalizeDate(
                            b.practiceDate
                        );

                    if (dateA !== dateB) {
                        return dateA.localeCompare(
                            dateB
                        );
                    }

                    if (
                        a.practiceTime !==
                        b.practiceTime
                    ) {
                        return (
                            (a.practiceTime ?? 0) -
                            (b.practiceTime ?? 0)
                        );
                    }

                    return (
                        (a.practiceLocation ??
                            0) -
                        (b.practiceLocation ??
                            0)
                    );
                });
        }, [
            practices,
            countryFilter,
            dateFilter,
            timeFilter,
            locationFilter,
        ]);

    // ---------------------------------------------------------
    // TOTALS
    // ---------------------------------------------------------

    const totals = useMemo(() => {
        return filteredPractices.reduce(
            (acc, practice) => {
                acc.bookings += 1;

                acc.courts +=
                    practice.numberOfCourts ??
                    0;

                acc.players +=
                    practice.numberOfPlayers ??
                    0;

                return acc;
            },
            {
                bookings: 0,
                courts: 0,
                players: 0,
            }
        );
    }, [filteredPractices]);

    // ---------------------------------------------------------
    // GROUP BY DATE + TIME + LOCATION
    // ---------------------------------------------------------

    const groupedPractices =
        useMemo(() => {
            const map = new Map<
                string,
                GroupedPractice
            >();

            filteredPractices.forEach(
                (practice) => {
                    const date =
                        normalizeDate(
                            practice.practiceDate
                        );

                    const time =
                        practice.practiceTime;

                    const location =
                        practice.practiceLocation;

                    const key =
                        `${date}-${time ?? "none"}-${location ?? "none"}`;

                    if (!map.has(key)) {
                        map.set(key, {
                            key,

                            date,

                            timeValue: time,

                            timeText:
                                getPracticeTimeText(
                                    time
                                ),

                            locationValue:
                                location,

                            locationText:
                                getPracticeLocationText(
                                    location
                                ),

                            bookings: [],

                            totalCourts: 0,

                            totalPlayers: 0,
                        });
                    }

                    const group =
                        map.get(key)!;

                    group.bookings.push(
                        practice
                    );

                    group.totalCourts +=
                        practice.numberOfCourts ??
                        0;

                    group.totalPlayers +=
                        practice.numberOfPlayers ??
                        0;
                }
            );

            return Array.from(
                map.values()
            ).sort((a, b) => {
                if (a.date !== b.date) {
                    return a.date.localeCompare(
                        b.date
                    );
                }

                if (
                    a.timeValue !==
                    b.timeValue
                ) {
                    return (
                        (a.timeValue ?? 0) -
                        (b.timeValue ?? 0)
                    );
                }

                return a.locationText.localeCompare(
                    b.locationText
                );
            });
        }, [
            filteredPractices,
            practiceTimes,
            practiceLocations,
        ]);

    // ---------------------------------------------------------
    // COUNTRY SUMMARY
    // ---------------------------------------------------------

    const countrySummary =
        useMemo<CountrySummary[]>(() => {
            const map =
                new Map<
                    string,
                    CountrySummary
                >();

            filteredPractices.forEach(
                (practice) => {
                    const country =
                        practice.country?.trim() ||
                        "Ukendt";

                    if (!map.has(country)) {
                        map.set(country, {
                            country,
                            bookings: 0,
                            courts: 0,
                            players: 0,
                        });
                    }

                    const item =
                        map.get(country)!;

                    item.bookings += 1;

                    item.courts +=
                        practice.numberOfCourts ??
                        0;

                    item.players +=
                        practice.numberOfPlayers ??
                        0;
                }
            );

            return Array.from(
                map.values()
            ).sort(
                (a, b) =>
                    b.courts - a.courts
            );
        }, [filteredPractices]);

    // ---------------------------------------------------------
    // LOCATION SUMMARY
    // ---------------------------------------------------------

    const locationSummary =
        useMemo<LocationSummary[]>(() => {
            const map =
                new Map<
                    string,
                    LocationSummary
                >();

            filteredPractices.forEach(
                (practice) => {
                    const locationValue =
                        practice.practiceLocation;

                    const key =
                        locationValue !==
                            undefined &&
                            locationValue !== null
                            ? String(
                                locationValue
                            )
                            : "none";

                    if (!map.has(key)) {
                        map.set(key, {
                            locationValue,

                            location:
                                getPracticeLocationText(
                                    locationValue
                                ),

                            bookings: 0,

                            courts: 0,

                            players: 0,
                        });
                    }

                    const item =
                        map.get(key)!;

                    item.bookings += 1;

                    item.courts +=
                        practice.numberOfCourts ??
                        0;

                    item.players +=
                        practice.numberOfPlayers ??
                        0;
                }
            );

            return Array.from(
                map.values()
            ).sort(
                (a, b) =>
                    b.courts - a.courts
            );
        }, [
            filteredPractices,
            practiceLocations,
        ]);

    // ---------------------------------------------------------
    // RESET FILTERS
    // ---------------------------------------------------------

    const resetFilters = () => {
        setCountryFilter("");
        setDateFilter(null);
        setTimeFilter(null);
        setLocationFilter(null);
    };

    // ---------------------------------------------------------
    // CURRENT EVENT
    // ---------------------------------------------------------

    const selectedEvent =
        useMemo(() => {
            if (!selectedEventId) {
                return undefined;
            }

            return events.find(
                (event) =>
                    String(event.eventID) ===
                    selectedEventId
            );
        }, [
            events,
            selectedEventId,
        ]);

    // ---------------------------------------------------------
    // EXPORT TO EXCEL
    // ---------------------------------------------------------

    const exportPracticesToExcel = () => {
        if (filteredPractices.length === 0) {
            return;
        }

        const rows = filteredPractices.map((practice) => ({
            "Booking ID": practice.practiceId ?? "",
            "Land": practice.country ?? "",
            "Dato": formatDate(practice.practiceDate),
            "Tidspunkt": getPracticeTimeText(practice.practiceTime),
            "Træningssted": getPracticeLocationText(practice.practiceLocation),
            "Antal baner": practice.numberOfCourts ?? 0,
            "Antal spillere": practice.numberOfPlayers ?? 0,
            "Varighed": getPracticeMinutesText(practice.numberOfMinuts),
            "User ID": practice.userId ?? "",
            "Oprettet": practice.createdDate
                ? new Date(practice.createdDate).toLocaleString("da-DK")
                : "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);

        // Kolonnebredder
        worksheet["!cols"] = [
            { wch: 14 },
            { wch: 22 },
            { wch: 16 },
            { wch: 16 },
            { wch: 24 },
            { wch: 15 },
            { wch: 17 },
            { wch: 18 },
            { wch: 14 },
            { wch: 22 },
        ];

        // Aktivér Excel-filter
        worksheet["!autofilter"] = {
            ref: worksheet["!ref"]!,
        };

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Bookingønsker"
        );

        const eventName = selectedEvent?.eventName ?? "Event";

        const safeEventName = eventName
            .replace(/[\\/:*?"<>|]/g, "")
            .replace(/\s+/g, "_");

        XLSX.writeFile(
            workbook,
            `Traeningsbookinger_${safeEventName}.xlsx`
        );
    };

    // ---------------------------------------------------------
    // RENDER
    // ---------------------------------------------------------

    return (
        <div>
            <HeroSectionV2
                titleEn="Training plan"
                titleDa="Træningsplan"
                descriptionEn="See practice requests"
                descriptionDa="Se træningsønsker"
                imageSrc={Billede1}
            />

            <Container
                size="xl"
                py="xl"
            >
                <Stack gap="lg">

                    {/* =========================================================
                    TITLE
                ========================================================= */}

                    <Group
                        justify="space-between"
                        align="flex-end"
                    >
                        <div>
                            <Title order={1}>
                                Træningsbookinger
                            </Title>

                            <Text c="dimmed">
                                Administrativt overblik over ønsker
                                til træningsbaner.
                            </Text>
                        </div>

                        <Group>
                            <Button
                                disabled={!selectedEventId}
                                onClick={() => {
                                    if (!selectedEventId) {
                                        return;
                                    }

                                    navigate(
                                        `/Admin/PracticePlanner/${selectedEventId}/0`
                                    );
                                }}
                            >
                                Træningsplan
                            </Button>

                            <Button
                                variant="default"
                                onClick={refreshData}
                                loading={loadingPractices}
                                disabled={!selectedEventId}
                            >
                                Genindlæs
                            </Button>
                        </Group>
                    </Group>


                    {/* =========================================================
                    ERROR
                ========================================================= */}

                    {error && (
                        <Alert
                            color="red"
                            title="Fejl"
                        >
                            {error}
                        </Alert>
                    )}


                    {/* =========================================================
                    EVENT SELECTOR
                ========================================================= */}

                    <Paper
                        withBorder
                        p="lg"
                        radius="md"
                    >
                        <Group
                            align="end"
                            justify="space-between"
                        >
                            <Group align="end">
                                <Select
                                    label="Event"
                                    placeholder={
                                        loadingEvents
                                            ? "Henter events..."
                                            : "Vælg event"
                                    }
                                    searchable
                                    clearable={false}
                                    value={selectedEventId}
                                    onChange={setSelectedEventId}
                                    data={events.map(
                                        (event) => ({
                                            value: String(
                                                event.eventID
                                            ),
                                            label:
                                                event.eventName,
                                        })
                                    )}
                                    disabled={loadingEvents}
                                    style={{
                                        minWidth: 320,
                                    }}
                                />

                                {loadingEvents && (
                                    <Loader size="sm" />
                                )}
                            </Group>

                            {selectedEvent && (
                                <Text
                                    size="sm"
                                    c="dimmed"
                                >
                                    Event ID:{" "}
                                    {selectedEvent.eventID}
                                </Text>
                            )}
                        </Group>
                    </Paper>


                    {/* =========================================================
                    MAIN DASHBOARD
                ========================================================= */}

                    <Grid
                        align="start"
                        gutter="lg"
                    >

                        {/* =====================================================
                                LEFT SETTINGS SIDEBAR
                            ===================================================== */}

                        <Grid.Col
                            span={{
                                base: 12,
                                lg: 3,
                            }}
                        >
                            {selectedEventId && (
                                <Stack
                                    gap="md"
                                    style={{
                                        position: "sticky",
                                        top: 20,
                                    }}
                                >

                                    {/* SETTINGS MENU */}

                                    <Paper
                                        withBorder
                                        p="md"
                                        radius="md"
                                    >
                                        <Stack gap="xs">

                                            <div
                                                style={{
                                                    paddingBottom: 6,
                                                }}
                                            >
                                                <Text
                                                    fw={700}
                                                    size="sm"
                                                >
                                                    INDSTILLINGER
                                                </Text>

                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                >
                                                    Konfiguration for dette event
                                                </Text>
                                            </div>
                                            {/* DATES BUTTON */}

                                            <Button
                                                fullWidth
                                                variant={
                                                    datesOpen
                                                        ? "light"
                                                        : "subtle"
                                                }
                                                onClick={() =>
                                                    setDatesOpen(
                                                        (value) => !value
                                                    )
                                                }
                                                styles={{
                                                    inner: {
                                                        justifyContent:
                                                            "space-between",
                                                    },
                                                }}
                                            >
                                                <Group
                                                    gap="xs"
                                                    wrap="nowrap"
                                                >
                                                    <Text
                                                        size="sm"
                                                        fw={
                                                            datesOpen
                                                                ? 600
                                                                : 500
                                                        }
                                                    >
                                                        Træningsdatoer
                                                    </Text>

                                                    <Badge
                                                        size="sm"
                                                        variant="light"
                                                    >
                                                        {practiceDates.length}
                                                    </Badge>
                                                </Group>
                                            </Button>

                                            {/* LOCATIONS BUTTON */}

                                            <Button
                                                fullWidth
                                                variant={
                                                    locationsOpen
                                                        ? "light"
                                                        : "subtle"
                                                }
                                                onClick={() =>
                                                    setLocationsOpen(
                                                        (value) =>
                                                            !value
                                                    )
                                                }
                                                styles={{
                                                    inner: {
                                                        justifyContent:
                                                            "space-between",
                                                    },
                                                }}
                                            >
                                                <Group
                                                    gap="xs"
                                                    wrap="nowrap"
                                                >
                                                    <Text
                                                        size="sm"
                                                        fw={
                                                            locationsOpen
                                                                ? 600
                                                                : 500
                                                        }
                                                    >
                                                        Træningssteder
                                                    </Text>

                                                    <Badge
                                                        size="sm"
                                                        variant="light"
                                                    >
                                                        {
                                                            practiceLocations.length
                                                        }
                                                    </Badge>
                                                </Group>
                                            </Button>


                                            {/* TIMES BUTTON */}

                                            <Button
                                                fullWidth
                                                variant={
                                                    timesOpen
                                                        ? "light"
                                                        : "subtle"
                                                }
                                                onClick={() =>
                                                    setTimesOpen(
                                                        (value) =>
                                                            !value
                                                    )
                                                }
                                                styles={{
                                                    inner: {
                                                        justifyContent:
                                                            "space-between",
                                                    },
                                                }}
                                            >
                                                <Group
                                                    gap="xs"
                                                    wrap="nowrap"
                                                >
                                                    <Text
                                                        size="sm"
                                                        fw={
                                                            timesOpen
                                                                ? 600
                                                                : 500
                                                        }
                                                    >
                                                        Tidspunkter
                                                    </Text>

                                                    <Badge
                                                        size="sm"
                                                        variant="light"
                                                    >
                                                        {
                                                            practiceTimes.length
                                                        }
                                                    </Badge>
                                                </Group>
                                            </Button>


                                            {/* MINUTES BUTTON */}

                                            <Button
                                                fullWidth
                                                variant={
                                                    minutesOpen
                                                        ? "light"
                                                        : "subtle"
                                                }
                                                onClick={() =>
                                                    setMinutesOpen(
                                                        (value) =>
                                                            !value
                                                    )
                                                }
                                                styles={{
                                                    inner: {
                                                        justifyContent:
                                                            "space-between",
                                                    },
                                                }}
                                            >
                                                <Group
                                                    gap="xs"
                                                    wrap="nowrap"
                                                >
                                                    <Text
                                                        size="sm"
                                                        fw={
                                                            minutesOpen
                                                                ? 600
                                                                : 500
                                                        }
                                                    >
                                                        Varigheder
                                                    </Text>

                                                    <Badge
                                                        size="sm"
                                                        variant="light"
                                                    >
                                                        {
                                                            practiceMinutes.length
                                                        }
                                                    </Badge>
                                                </Group>
                                            </Button>

                                        </Stack>
                                    </Paper>

                                    {/* =================================================
    DATE SETTINGS
================================================= */}

                                    <Collapse in={datesOpen}>
                                        <Paper
                                            withBorder
                                            p="md"
                                            radius="md"
                                        >
                                            <Stack gap="md">

                                                <div>
                                                    <Title order={4}>
                                                        Træningsdatoer
                                                    </Title>

                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                    >
                                                        Opret og administrer de datoer,
                                                        hvor brugerne kan booke træning.
                                                    </Text>
                                                </div>

                                                {/* CREATE DATE */}

                                                <Stack gap="xs">
                                                    <TextInput
                                                        type="date"
                                                        label="Ny dato"
                                                        value={newPracticeDate}
                                                        onChange={(event) =>
                                                            setNewPracticeDate(
                                                                event.currentTarget.value
                                                            )
                                                        }
                                                    />

                                                    <Button
                                                        fullWidth
                                                        onClick={createPracticeDate}
                                                        loading={savingPracticeOptions}
                                                    >
                                                        Opret dato
                                                    </Button>
                                                </Stack>

                                                {/* EXISTING DATES */}

                                                {practiceDates.length === 0 ? (
                                                    <Alert
                                                        color="yellow"
                                                        title="Ingen træningsdatoer"
                                                    >
                                                        Der er endnu ikke oprettet
                                                        nogen træningsdatoer til
                                                        dette event.
                                                    </Alert>
                                                ) : (
                                                    <Stack gap="xs">
                                                        {practiceDates
                                                            .slice()
                                                            .sort((a, b) =>
                                                                normalizeDate(a.date).localeCompare(
                                                                    normalizeDate(b.date)
                                                                )
                                                            )
                                                            .map((practiceDate) => {
                                                                const editing =
                                                                    editingPracticeDateId ===
                                                                    practiceDate.value;

                                                                return (
                                                                    <Card
                                                                        key={practiceDate.value}
                                                                        withBorder
                                                                        radius="sm"
                                                                        padding="sm"
                                                                    >
                                                                        {editing ? (
                                                                            <Stack gap="xs">
                                                                                <TextInput
                                                                                    type="date"
                                                                                    label="Dato"
                                                                                    value={
                                                                                        editingPracticeDate
                                                                                    }
                                                                                    onChange={(event) =>
                                                                                        setEditingPracticeDate(
                                                                                            event.currentTarget.value
                                                                                        )
                                                                                    }
                                                                                />

                                                                                <Group
                                                                                    gap="xs"
                                                                                    grow
                                                                                >
                                                                                    <Button
                                                                                        size="sm"
                                                                                        onClick={
                                                                                            updatePracticeDate
                                                                                        }
                                                                                        loading={
                                                                                            savingPracticeOptions
                                                                                        }
                                                                                    >
                                                                                        Gem
                                                                                    </Button>

                                                                                    <Button
                                                                                        size="sm"
                                                                                        variant="default"
                                                                                        onClick={
                                                                                            cancelEditPracticeDate
                                                                                        }
                                                                                    >
                                                                                        Annuller
                                                                                    </Button>
                                                                                </Group>
                                                                            </Stack>
                                                                        ) : (
                                                                            <Group
                                                                                justify="space-between"
                                                                                wrap="nowrap"
                                                                            >
                                                                                <Text
                                                                                    size="sm"
                                                                                    fw={600}
                                                                                >
                                                                                    {formatDate(
                                                                                        practiceDate.date
                                                                                    )}
                                                                                </Text>

                                                                                <Group
                                                                                    gap="xs"
                                                                                    wrap="nowrap"
                                                                                >
                                                                                    <Button
                                                                                        size="xs"
                                                                                        variant="light"
                                                                                        onClick={() =>
                                                                                            startEditPracticeDate(
                                                                                                practiceDate
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        Rediger
                                                                                    </Button>

                                                                                    <Button
                                                                                        size="xs"
                                                                                        color="red"
                                                                                        variant="light"
                                                                                        onClick={() =>
                                                                                            deletePracticeDate(
                                                                                                practiceDate.value
                                                                                            )
                                                                                        }
                                                                                        loading={
                                                                                            savingPracticeOptions
                                                                                        }
                                                                                    >
                                                                                        Slet
                                                                                    </Button>
                                                                                </Group>
                                                                            </Group>
                                                                        )}
                                                                    </Card>
                                                                );
                                                            })}
                                                    </Stack>
                                                )}

                                            </Stack>
                                        </Paper>
                                    </Collapse>

                                    {/* =================================================
                                    LOCATION SETTINGS
                                ================================================= */}

                                    <Collapse in={locationsOpen}>
                                        <Paper
                                            withBorder
                                            p="md"
                                            radius="md"
                                        >
                                            <Stack gap="md">

                                                <div>
                                                    <Title order={4}>
                                                        Træningssteder
                                                    </Title>

                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                    >
                                                        Opret og administrer
                                                        træningssteder.
                                                    </Text>
                                                </div>


                                                {/* CREATE LOCATION */}

                                                <Stack gap="xs">
                                                    <TextInput
                                                        label="Nyt træningssted"
                                                        placeholder="Fx Hal 1"
                                                        value={
                                                            newLocationName
                                                        }
                                                        onChange={(
                                                            event
                                                        ) =>
                                                            setNewLocationName(
                                                                event
                                                                    .currentTarget
                                                                    .value
                                                            )
                                                        }
                                                    />

                                                    <Button
                                                        fullWidth
                                                        onClick={
                                                            createPracticeLocation
                                                        }
                                                        loading={
                                                            savingLocation
                                                        }
                                                    >
                                                        Opret træningssted
                                                    </Button>
                                                </Stack>


                                                {/* EXISTING LOCATIONS */}

                                                {practiceLocations.length ===
                                                    0 ? (
                                                    <Alert
                                                        color="yellow"
                                                        title="Ingen træningssteder"
                                                    >
                                                        Der er endnu ikke
                                                        oprettet nogen
                                                        træningssteder til
                                                        dette event.
                                                    </Alert>
                                                ) : (
                                                    <Stack gap="xs">
                                                        {practiceLocations.map(
                                                            (
                                                                location
                                                            ) => {
                                                                const editing =
                                                                    editingLocationId ===
                                                                    location.value;

                                                                return (
                                                                    <Card
                                                                        key={
                                                                            location.value
                                                                        }
                                                                        withBorder
                                                                        radius="sm"
                                                                        padding="sm"
                                                                    >
                                                                        {editing ? (
                                                                            <Stack gap="xs">
                                                                                <TextInput
                                                                                    label="Navn"
                                                                                    value={
                                                                                        editingLocationName
                                                                                    }
                                                                                    onChange={(
                                                                                        event
                                                                                    ) =>
                                                                                        setEditingLocationName(
                                                                                            event
                                                                                                .currentTarget
                                                                                                .value
                                                                                        )
                                                                                    }
                                                                                />

                                                                                <Group
                                                                                    gap="xs"
                                                                                    grow
                                                                                >
                                                                                    <Button
                                                                                        size="sm"
                                                                                        onClick={
                                                                                            updatePracticeLocation
                                                                                        }
                                                                                        loading={
                                                                                            savingLocation
                                                                                        }
                                                                                    >
                                                                                        Gem
                                                                                    </Button>

                                                                                    <Button
                                                                                        size="sm"
                                                                                        variant="default"
                                                                                        onClick={
                                                                                            cancelEditLocation
                                                                                        }
                                                                                    >
                                                                                        Annuller
                                                                                    </Button>
                                                                                </Group>
                                                                            </Stack>
                                                                        ) : (
                                                                            <Group
                                                                                justify="space-between"
                                                                                wrap="nowrap"
                                                                            >
                                                                                <Text
                                                                                    size="sm"
                                                                                    fw={
                                                                                        600
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        location.text
                                                                                    }
                                                                                </Text>

                                                                                <Group
                                                                                    gap="xs"
                                                                                    wrap="nowrap"
                                                                                >
                                                                                    <Button
                                                                                        size="xs"
                                                                                        variant="light"
                                                                                        onClick={() =>
                                                                                            startEditLocation(
                                                                                                location
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        Rediger
                                                                                    </Button>

                                                                                    <Button
                                                                                        size="xs"
                                                                                        color="red"
                                                                                        variant="light"
                                                                                        onClick={() =>
                                                                                            deletePracticeLocation(
                                                                                                location.value
                                                                                            )
                                                                                        }
                                                                                        loading={
                                                                                            savingLocation
                                                                                        }
                                                                                    >
                                                                                        Slet
                                                                                    </Button>
                                                                                </Group>
                                                                            </Group>
                                                                        )}
                                                                    </Card>
                                                                );
                                                            }
                                                        )}
                                                    </Stack>
                                                )}

                                            </Stack>
                                        </Paper>
                                    </Collapse>


                                    {/* =================================================
                                    TIME SETTINGS
                                ================================================= */}

                                    <Collapse in={timesOpen}>
                                        <Paper
                                            withBorder
                                            p="md"
                                            radius="md"
                                        >
                                            <Stack gap="md">

                                                <div>
                                                    <Title order={4}>
                                                        Træningstidspunkter
                                                    </Title>

                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                    >
                                                        Opret og administrer
                                                        tidspunkter.
                                                    </Text>
                                                </div>


                                                {/* CREATE TIME */}

                                                <Stack gap="xs">
                                                    <MultiSelect
                                                        label="Tidspunkter"
                                                        placeholder="Vælg tidspunkter"
                                                        searchable
                                                        clearable
                                                        value={selectedPracticeTimes}
                                                        onChange={setSelectedPracticeTimes}
                                                        data={Array.from(
                                                            { length: 24 },
                                                            (_, hour) => {
                                                                const time = `${hour
                                                                    .toString()
                                                                    .padStart(2, "0")}:00`;

                                                                return {
                                                                    value: time,
                                                                    label: time,
                                                                };
                                                            }
                                                        )}
                                                    />

                                                    <Button
                                                        fullWidth
                                                        onClick={async () => {
                                                            for (
                                                                const time of selectedPracticeTimes
                                                            ) {
                                                                await createPracticeTime(time);
                                                            }

                                                            setSelectedPracticeTimes([]);
                                                        }}
                                                        loading={savingPracticeOptions}
                                                        disabled={
                                                            selectedPracticeTimes.length === 0
                                                        }
                                                    >
                                                        Opret tidspunkter
                                                    </Button>
                                                </Stack>


                                                {/* EXISTING TIMES */}

                                                {practiceTimes.length ===
                                                    0 ? (
                                                    <Alert
                                                        color="yellow"
                                                        title="Ingen tidspunkter"
                                                    >
                                                        Der er endnu ikke
                                                        oprettet nogen
                                                        træningstidspunkter
                                                        til dette event.
                                                    </Alert>
                                                ) : (
                                                    <Stack gap="xs">
                                                        {practiceTimes.map(
                                                            (time) => {
                                                                const editing =
                                                                    editingPracticeTimeId ===
                                                                    time.value;

                                                                return (
                                                                    <Card
                                                                        key={
                                                                            time.value
                                                                        }
                                                                        withBorder
                                                                        radius="sm"
                                                                        padding="sm"
                                                                    >
                                                                        {editing ? (
                                                                            <Stack gap="xs">
                                                                                <TextInput
                                                                                    label="Tidspunkt"
                                                                                    value={
                                                                                        editingPracticeTimeText
                                                                                    }
                                                                                    onChange={(
                                                                                        event
                                                                                    ) =>
                                                                                        setEditingPracticeTimeText(
                                                                                            event
                                                                                                .currentTarget
                                                                                                .value
                                                                                        )
                                                                                    }
                                                                                />

                                                                                <Group
                                                                                    gap="xs"
                                                                                    grow
                                                                                >
                                                                                    <Button
                                                                                        size="sm"
                                                                                        onClick={
                                                                                            updatePracticeTime
                                                                                        }
                                                                                        loading={
                                                                                            savingPracticeOptions
                                                                                        }
                                                                                    >
                                                                                        Gem
                                                                                    </Button>

                                                                                    <Button
                                                                                        size="sm"
                                                                                        variant="default"
                                                                                        onClick={() => {
                                                                                            setEditingPracticeTimeId(
                                                                                                null
                                                                                            );

                                                                                            setEditingPracticeTimeText(
                                                                                                ""
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        Annuller
                                                                                    </Button>
                                                                                </Group>
                                                                            </Stack>
                                                                        ) : (
                                                                            <Group
                                                                                justify="space-between"
                                                                                wrap="nowrap"
                                                                            >
                                                                                <Text
                                                                                    size="sm"
                                                                                    fw={
                                                                                        600
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        time.text
                                                                                    }
                                                                                </Text>

                                                                                <Group
                                                                                    gap="xs"
                                                                                    wrap="nowrap"
                                                                                >
                                                                                    <Button
                                                                                        size="xs"
                                                                                        variant="light"
                                                                                        onClick={() => {
                                                                                            setEditingPracticeTimeId(
                                                                                                time.value
                                                                                            );

                                                                                            setEditingPracticeTimeText(
                                                                                                time.text
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        Rediger
                                                                                    </Button>

                                                                                    <Button
                                                                                        size="xs"
                                                                                        color="red"
                                                                                        variant="light"
                                                                                        onClick={() =>
                                                                                            deletePracticeTime(
                                                                                                time.value
                                                                                            )
                                                                                        }
                                                                                        loading={
                                                                                            savingPracticeOptions
                                                                                        }
                                                                                    >
                                                                                        Slet
                                                                                    </Button>
                                                                                </Group>
                                                                            </Group>
                                                                        )}
                                                                    </Card>
                                                                );
                                                            }
                                                        )}
                                                    </Stack>
                                                )}

                                            </Stack>
                                        </Paper>
                                    </Collapse>


                                    {/* =================================================
                                    MINUTES SETTINGS
                                ================================================= */}

                                    <Collapse in={minutesOpen}>
                                        <Paper
                                            withBorder
                                            p="md"
                                            radius="md"
                                        >
                                            <Stack gap="md">

                                                <div>
                                                    <Title order={4}>
                                                        Træningsvarigheder
                                                    </Title>

                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                    >
                                                        Opret og administrer
                                                        varigheder.
                                                    </Text>
                                                </div>


                                                {/* CREATE MINUTES */}

                                                <Stack gap="xs">
                                                    <TextInput
                                                        label="Ny varighed"
                                                        placeholder="Fx 60 min."
                                                        value={
                                                            newPracticeMinutes
                                                        }
                                                        onChange={(event) =>
                                                            setNewPracticeMinutes(
                                                                event
                                                                    .currentTarget
                                                                    .value
                                                            )
                                                        }
                                                    />

                                                    <Button
                                                        fullWidth
                                                        onClick={
                                                            createPracticeMinutes
                                                        }
                                                        loading={
                                                            savingPracticeOptions
                                                        }
                                                    >
                                                        Opret varighed
                                                    </Button>
                                                </Stack>


                                                {/* EXISTING MINUTES */}

                                                {practiceMinutes.length ===
                                                    0 ? (
                                                    <Alert
                                                        color="yellow"
                                                        title="Ingen varigheder"
                                                    >
                                                        Der er endnu ikke
                                                        oprettet nogen
                                                        træningsvarigheder
                                                        til dette event.
                                                    </Alert>
                                                ) : (
                                                    <Stack gap="xs">
                                                        {practiceMinutes.map(
                                                            (minutes) => {
                                                                const editing =
                                                                    editingPracticeMinutesId ===
                                                                    minutes.value;

                                                                return (
                                                                    <Card
                                                                        key={
                                                                            minutes.value
                                                                        }
                                                                        withBorder
                                                                        radius="sm"
                                                                        padding="sm"
                                                                    >
                                                                        {editing ? (
                                                                            <Stack gap="xs">
                                                                                <TextInput
                                                                                    label="Varighed"
                                                                                    value={
                                                                                        editingPracticeMinutesText
                                                                                    }
                                                                                    onChange={(
                                                                                        event
                                                                                    ) =>
                                                                                        setEditingPracticeMinutesText(
                                                                                            event
                                                                                                .currentTarget
                                                                                                .value
                                                                                        )
                                                                                    }
                                                                                />

                                                                                <Group
                                                                                    gap="xs"
                                                                                    grow
                                                                                >
                                                                                    <Button
                                                                                        size="sm"
                                                                                        onClick={
                                                                                            updatePracticeMinutes
                                                                                        }
                                                                                        loading={
                                                                                            savingPracticeOptions
                                                                                        }
                                                                                    >
                                                                                        Gem
                                                                                    </Button>

                                                                                    <Button
                                                                                        size="sm"
                                                                                        variant="default"
                                                                                        onClick={() => {
                                                                                            setEditingPracticeMinutesId(
                                                                                                null
                                                                                            );

                                                                                            setEditingPracticeMinutesText(
                                                                                                ""
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        Annuller
                                                                                    </Button>
                                                                                </Group>
                                                                            </Stack>
                                                                        ) : (
                                                                            <Group
                                                                                justify="space-between"
                                                                                wrap="nowrap"
                                                                            >
                                                                                <Text
                                                                                    size="sm"
                                                                                    fw={
                                                                                        600
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        minutes.text
                                                                                    }
                                                                                </Text>

                                                                                <Group
                                                                                    gap="xs"
                                                                                    wrap="nowrap"
                                                                                >
                                                                                    <Button
                                                                                        size="xs"
                                                                                        variant="light"
                                                                                        onClick={() => {
                                                                                            setEditingPracticeMinutesId(
                                                                                                minutes.value
                                                                                            );

                                                                                            setEditingPracticeMinutesText(
                                                                                                minutes.text
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        Rediger
                                                                                    </Button>

                                                                                    <Button
                                                                                        size="xs"
                                                                                        color="red"
                                                                                        variant="light"
                                                                                        onClick={() =>
                                                                                            deletePracticeMinutes(
                                                                                                minutes.value
                                                                                            )
                                                                                        }
                                                                                        loading={
                                                                                            savingPracticeOptions
                                                                                        }
                                                                                    >
                                                                                        Slet
                                                                                    </Button>
                                                                                </Group>
                                                                            </Group>
                                                                        )}
                                                                    </Card>
                                                                );
                                                            }
                                                        )}
                                                    </Stack>
                                                )}

                                            </Stack>
                                        </Paper>
                                    </Collapse>

                                </Stack>
                            )}
                        </Grid.Col>


                        {/* =====================================================
                        RIGHT STATISTICS DASHBOARD
                    ===================================================== */}

                        <Grid.Col
                            span={{
                                base: 12,
                                lg: 9,
                            }}
                        >
                            <Stack gap="lg">

                            {/* =================================================
                                FILTERS
                            ================================================= */}

                                <Paper
                                    withBorder
                                    p="lg"
                                    radius="md"
                                >
                                    <Stack gap="md">

                                        <Group
                                            justify="space-between"
                                            align="flex-start"
                                        >
                                            <div>
                                                <Title order={3}>
                                                    Filtre
                                                </Title>

                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Filtrér på land,
                                                    dato, tidspunkt
                                                    eller træningssted.
                                                </Text>
                                            </div>

                                            <Button
                                                variant="default"
                                                onClick={
                                                    resetFilters
                                                }
                                            >
                                                Nulstil filtre
                                            </Button>
                                        </Group>


                                        <Grid>

                                            <Grid.Col
                                                span={{
                                                    base: 12,
                                                    sm: 6,
                                                    lg: 3,
                                                }}
                                            >
                                                <TextInput
                                                    label="Land"
                                                    placeholder="Fx Denmark, China..."
                                                    value={
                                                        countryFilter
                                                    }
                                                    onChange={(event) =>
                                                        setCountryFilter(
                                                            event
                                                                .currentTarget
                                                                .value
                                                        )
                                                    }
                                                />
                                            </Grid.Col>


                                            <Grid.Col
                                                span={{
                                                    base: 12,
                                                    sm: 6,
                                                    lg: 3,
                                                }}
                                            >
                                                <Select
                                                    label="Dato"
                                                    placeholder="Alle datoer"
                                                    clearable
                                                    searchable
                                                    value={
                                                        dateFilter
                                                    }
                                                    onChange={
                                                        setDateFilter
                                                    }
                                                    data={
                                                        availableDates
                                                    }
                                                />
                                            </Grid.Col>


                                            <Grid.Col
                                                span={{
                                                    base: 12,
                                                    sm: 6,
                                                    lg: 3,
                                                }}
                                            >
                                                <Select
                                                    label="Tidspunkt"
                                                    placeholder="Alle tidspunkter"
                                                    searchable
                                                    clearable
                                                    value={timeFilter}
                                                    onChange={setTimeFilter}
                                                    data={Array.from(
                                                        { length: 24 },
                                                        (_, hour) => {
                                                            const time = `${hour
                                                                .toString()
                                                                .padStart(2, "0")}:00`;

                                                            return {
                                                                value: time,
                                                                label: time,
                                                            };
                                                        }
                                                    )}
                                                />
                                            </Grid.Col>


                                            <Grid.Col
                                                span={{
                                                    base: 12,
                                                    sm: 6,
                                                    lg: 3,
                                                }}
                                            >
                                                <Select
                                                    label="Træningssted"
                                                    placeholder="Alle steder"
                                                    clearable
                                                    searchable
                                                    value={
                                                        locationFilter
                                                    }
                                                    onChange={
                                                        setLocationFilter
                                                    }
                                                    data={practiceLocations.map(
                                                        (location) => ({
                                                            value:
                                                                String(
                                                                    location.value
                                                                ),
                                                            label:
                                                                location.text,
                                                        })
                                                    )}
                                                />
                                            </Grid.Col>

                                        </Grid>
                                    </Stack>
                                </Paper>


                                {/* =================================================
                                LOADING / EMPTY / STATISTICS
                            ================================================= */}

                                {loadingPractices ? (
                                    <Paper
                                        withBorder
                                        p={50}
                                        radius="md"
                                    >
                                        <Group justify="center">
                                            <Loader />

                                            <Text>
                                                Henter
                                                træningsbookinger...
                                            </Text>
                                        </Group>
                                    </Paper>

                                ) : practices.length === 0 ? (

                                    <Paper
                                        withBorder
                                        p={50}
                                        radius="md"
                                    >
                                        <Stack
                                            align="center"
                                            gap="xs"
                                        >
                                            <Title order={3}>
                                                Ingen
                                                træningsbookinger
                                            </Title>

                                            <Text c="dimmed">
                                                Der er endnu
                                                ikke oprettet
                                                nogen ønsker
                                                til dette event.
                                            </Text>
                                        </Stack>
                                    </Paper>

                                ) : (

                                    <>

                                        {/* =========================================
                                        KPI
                                    ========================================= */}

                                        <SimpleGrid
                                            cols={{
                                                base: 2,
                                                md: 4,
                                            }}
                                            spacing="md"
                                        >

                                            <Card
                                                withBorder
                                                radius="md"
                                                padding="lg"
                                            >
                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Bookingønsker
                                                </Text>

                                                <Text
                                                    size="2rem"
                                                    fw={700}
                                                >
                                                    {
                                                        totals.bookings
                                                    }
                                                </Text>
                                            </Card>


                                            <Card
                                                withBorder
                                                radius="md"
                                                padding="lg"
                                            >
                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Ønskede baner
                                                </Text>

                                                <Text
                                                    size="2rem"
                                                    fw={700}
                                                >
                                                    {
                                                        totals.courts
                                                    }
                                                </Text>
                                            </Card>


                                            <Card
                                                withBorder
                                                radius="md"
                                                padding="lg"
                                            >
                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Spillere
                                                </Text>

                                                <Text
                                                    size="2rem"
                                                    fw={700}
                                                >
                                                    {
                                                        totals.players
                                                    }
                                                </Text>
                                            </Card>


                                            <Card
                                                withBorder
                                                radius="md"
                                                padding="lg"
                                            >
                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Lande
                                                </Text>

                                                <Text
                                                    size="2rem"
                                                    fw={700}
                                                >
                                                    {
                                                        countrySummary.length
                                                    }
                                                </Text>
                                            </Card>

                                        </SimpleGrid>


                                        {/* =========================================
                                        GROUPED OVERVIEW
                                    ========================================= */}

                                        <Paper
                                            withBorder
                                            p="lg"
                                            radius="md"
                                        >
                                            <Stack gap="md">

                                                <div>
                                                    <Title order={2}>
                                                        Overblik pr.
                                                        tidspunkt og hal
                                                    </Title>

                                                    <Text
                                                        size="sm"
                                                        c="dimmed"
                                                    >
                                                        Samlet efterspørgsel
                                                        fordelt på dato,
                                                        tidspunkt og
                                                        træningssted.
                                                    </Text>
                                                </div>


                                                {groupedPractices.length ===
                                                    0 ? (
                                                    <Text c="dimmed">
                                                        Ingen bookinger
                                                        matcher de valgte
                                                        filtre.
                                                    </Text>
                                                ) : (
                                                    <SimpleGrid
                                                        cols={{
                                                            base: 1,
                                                            md: 2,
                                                            xl: 3,
                                                        }}
                                                    >
                                                        {groupedPractices.map(
                                                            (group) => (
                                                                <Card
                                                                    key={
                                                                        group.key
                                                                    }
                                                                    withBorder
                                                                    radius="md"
                                                                    padding="lg"
                                                                >
                                                                    <Stack gap="sm">

                                                                        <Group
                                                                            justify="space-between"
                                                                            align="flex-start"
                                                                        >
                                                                            <div>
                                                                                <Text
                                                                                    fw={700}
                                                                                    size="lg"
                                                                                >
                                                                                    {formatDate(
                                                                                        group.date
                                                                                    )}
                                                                                </Text>

                                                                                <Text
                                                                                    fw={600}
                                                                                    size="xl"
                                                                                >
                                                                                    {
                                                                                        group.timeText
                                                                                    }
                                                                                </Text>

                                                                                <Text
                                                                                    size="sm"
                                                                                    c="dimmed"
                                                                                    fw={500}
                                                                                >
                                                                                    {
                                                                                        group.locationText
                                                                                    }
                                                                                </Text>
                                                                            </div>

                                                                            <Badge
                                                                                size="lg"
                                                                                variant="light"
                                                                            >
                                                                                {
                                                                                    group
                                                                                        .bookings
                                                                                        .length
                                                                                }{" "}
                                                                                ønsker
                                                                            </Badge>
                                                                        </Group>


                                                                        <Group gap="xl">

                                                                            <div>
                                                                                <Text
                                                                                    size="xs"
                                                                                    c="dimmed"
                                                                                >
                                                                                    Baner
                                                                                </Text>

                                                                                <Text
                                                                                    fw={700}
                                                                                    size="xl"
                                                                                >
                                                                                    {
                                                                                        group.totalCourts
                                                                                    }
                                                                                </Text>
                                                                            </div>


                                                                            <div>
                                                                                <Text
                                                                                    size="xs"
                                                                                    c="dimmed"
                                                                                >
                                                                                    Spillere
                                                                                </Text>

                                                                                <Text
                                                                                    fw={700}
                                                                                    size="xl"
                                                                                >
                                                                                    {
                                                                                        group.totalPlayers
                                                                                    }
                                                                                </Text>
                                                                            </div>

                                                                        </Group>


                                                                        <div>
                                                                            {group.bookings.map(
                                                                                (
                                                                                    booking
                                                                                ) => (
                                                                                    <Group
                                                                                        key={
                                                                                            booking.practiceId
                                                                                        }
                                                                                        justify="space-between"
                                                                                        style={{
                                                                                            borderTop:
                                                                                                "1px solid #eee",
                                                                                            paddingTop: 8,
                                                                                            marginTop: 8,
                                                                                        }}
                                                                                    >
                                                                                        <Text
                                                                                            size="sm"
                                                                                            fw={500}
                                                                                        >
                                                                                            {booking.country ||
                                                                                                "—"}
                                                                                        </Text>

                                                                                        <Text
                                                                                            size="sm"
                                                                                            c="dimmed"
                                                                                        >
                                                                                            {booking.numberOfCourts ??
                                                                                                0}{" "}
                                                                                            baner
                                                                                            {" · "}
                                                                                            {booking.numberOfPlayers ??
                                                                                                0}{" "}
                                                                                            spillere
                                                                                        </Text>
                                                                                    </Group>
                                                                                )
                                                                            )}
                                                                        </div>

                                                                    </Stack>
                                                                </Card>
                                                            )
                                                        )}
                                                    </SimpleGrid>
                                                )}

                                            </Stack>
                                        </Paper>


                                        {/* =========================================
                                        LOCATION + COUNTRY
                                    ========================================= */}

                                        <Grid>

                                            {/* LOCATION SUMMARY */}

                                            <Grid.Col
                                                span={{
                                                    base: 12,
                                                    lg: 6,
                                                }}
                                            >
                                                <Paper
                                                    withBorder
                                                    p="lg"
                                                    radius="md"
                                                    h="100%"
                                                >
                                                    <Stack gap="md">

                                                        <div>
                                                            <Title order={2}>
                                                                Overblik pr.
                                                                træningssted
                                                            </Title>

                                                            <Text
                                                                size="sm"
                                                                c="dimmed"
                                                            >
                                                                Samlet
                                                                efterspørgsel
                                                                på de enkelte
                                                                haller.
                                                            </Text>
                                                        </div>


                                                        <Table
                                                            striped
                                                            highlightOnHover
                                                        >
                                                            <Table.Thead>
                                                                <Table.Tr>
                                                                    <Table.Th>
                                                                        Sted
                                                                    </Table.Th>

                                                                    <Table.Th>
                                                                        Ønsker
                                                                    </Table.Th>

                                                                    <Table.Th>
                                                                        Baner
                                                                    </Table.Th>

                                                                    <Table.Th>
                                                                        Spillere
                                                                    </Table.Th>
                                                                </Table.Tr>
                                                            </Table.Thead>

                                                            <Table.Tbody>
                                                                {locationSummary.map(
                                                                    (
                                                                        item
                                                                    ) => (
                                                                        <Table.Tr
                                                                            key={
                                                                                item.locationValue ??
                                                                                "none"
                                                                            }
                                                                        >
                                                                            <Table.Td>
                                                                                <Text
                                                                                    fw={600}
                                                                                >
                                                                                    {
                                                                                        item.location
                                                                                    }
                                                                                </Text>
                                                                            </Table.Td>

                                                                            <Table.Td>
                                                                                {
                                                                                    item.bookings
                                                                                }
                                                                            </Table.Td>

                                                                            <Table.Td>
                                                                                {
                                                                                    item.courts
                                                                                }
                                                                            </Table.Td>

                                                                            <Table.Td>
                                                                                {
                                                                                    item.players
                                                                                }
                                                                            </Table.Td>
                                                                        </Table.Tr>
                                                                    )
                                                                )}
                                                            </Table.Tbody>
                                                        </Table>

                                                    </Stack>
                                                </Paper>
                                            </Grid.Col>


                                            {/* COUNTRY SUMMARY */}

                                            <Grid.Col
                                                span={{
                                                    base: 12,
                                                    lg: 6,
                                                }}
                                            >
                                                <Paper
                                                    withBorder
                                                    p="lg"
                                                    radius="md"
                                                    h="100%"
                                                >
                                                    <Stack gap="md">

                                                        <div>
                                                            <Title order={2}>
                                                                Overblik pr.
                                                                land
                                                            </Title>

                                                            <Text
                                                                size="sm"
                                                                c="dimmed"
                                                            >
                                                                Samlet
                                                                efterspørgsel
                                                                fra hvert land.
                                                            </Text>
                                                        </div>


                                                        <Table
                                                            striped
                                                            highlightOnHover
                                                        >
                                                            <Table.Thead>
                                                                <Table.Tr>
                                                                    <Table.Th>
                                                                        Land
                                                                    </Table.Th>

                                                                    <Table.Th>
                                                                        Ønsker
                                                                    </Table.Th>

                                                                    <Table.Th>
                                                                        Baner
                                                                    </Table.Th>

                                                                    <Table.Th>
                                                                        Spillere
                                                                    </Table.Th>
                                                                </Table.Tr>
                                                            </Table.Thead>

                                                            <Table.Tbody>
                                                                {countrySummary.map(
                                                                    (
                                                                        item
                                                                    ) => (
                                                                        <Table.Tr
                                                                            key={
                                                                                item.country
                                                                            }
                                                                        >
                                                                            <Table.Td>
                                                                                <Text
                                                                                    fw={600}
                                                                                >
                                                                                    {
                                                                                        item.country
                                                                                    }
                                                                                </Text>
                                                                            </Table.Td>

                                                                            <Table.Td>
                                                                                {
                                                                                    item.bookings
                                                                                }
                                                                            </Table.Td>

                                                                            <Table.Td>
                                                                                {
                                                                                    item.courts
                                                                                }
                                                                            </Table.Td>

                                                                            <Table.Td>
                                                                                {
                                                                                    item.players
                                                                                }
                                                                            </Table.Td>
                                                                        </Table.Tr>
                                                                    )
                                                                )}
                                                            </Table.Tbody>
                                                        </Table>

                                                    </Stack>
                                                </Paper>
                                            </Grid.Col>

                                        </Grid>


                                        {/* =========================================
                                        DETAILS
                                    ========================================= */}

                                        <Paper
                                            withBorder
                                            p="lg"
                                            radius="md"
                                        >
                                            <Stack gap="md">

                                                        <Group
                                                            justify="space-between"
                                                            align="center"
                                                            wrap="wrap"
                                                        >
                                                            <div>
                                                                <Title order={2}>
                                                                    Alle bookingønsker
                                                                </Title>

                                                                <Text size="sm" c="dimmed">
                                                                    {filteredPractices.length} af{" "}
                                                                    {practices.length} bookinger vises.
                                                                </Text>
                                                            </div>

                                                            <Button
                                                                color="green"
                                                                variant="filled"
                                                                onClick={exportPracticesToExcel}
                                                                disabled={
                                                                    loadingPractices ||
                                                                    filteredPractices.length === 0
                                                                }
                                                            >
                                                                Eksportér til Excel
                                                            </Button>
                                                        </Group>


                                                <div
                                                    style={{
                                                        overflowX:
                                                            "auto",
                                                    }}
                                                >
                                                    <Table
                                                        striped
                                                        highlightOnHover
                                                        withTableBorder
                                                    >
                                                        <Table.Thead>
                                                            <Table.Tr>

                                                                <Table.Th>
                                                                    Land
                                                                </Table.Th>

                                                                <Table.Th>
                                                                    Dato
                                                                </Table.Th>

                                                                <Table.Th>
                                                                    Tid
                                                                </Table.Th>

                                                                <Table.Th>
                                                                    Sted
                                                                </Table.Th>

                                                                <Table.Th>
                                                                    Baner
                                                                </Table.Th>

                                                                <Table.Th>
                                                                    Spillere
                                                                </Table.Th>

                                                                <Table.Th>
                                                                    Varighed
                                                                </Table.Th>

                                                                <Table.Th>
                                                                    User ID
                                                                </Table.Th>

                                                                <Table.Th>
                                                                    Oprettet
                                                                </Table.Th>

                                                            </Table.Tr>
                                                        </Table.Thead>


                                                        <Table.Tbody>

                                                            {filteredPractices.map(
                                                                (
                                                                    practice
                                                                ) => (
                                                                    <Table.Tr
                                                                        key={
                                                                            practice.practiceId
                                                                        }
                                                                    >

                                                                        <Table.Td>
                                                                            <Text
                                                                                fw={600}
                                                                            >
                                                                                {practice.country ||
                                                                                    "—"}
                                                                            </Text>
                                                                        </Table.Td>


                                                                        <Table.Td>
                                                                            {formatDate(
                                                                                practice.practiceDate
                                                                            )}
                                                                        </Table.Td>


                                                                        <Table.Td>
                                                                            <Badge variant="light">
                                                                                {getPracticeTimeText(
                                                                                    practice.practiceTime
                                                                                )}
                                                                            </Badge>
                                                                        </Table.Td>


                                                                        <Table.Td>
                                                                            {practice.practiceLocation !==
                                                                                undefined &&
                                                                                practice.practiceLocation !==
                                                                                null ? (
                                                                                <Badge
                                                                                    variant="outline"
                                                                                >
                                                                                    {getPracticeLocationText(
                                                                                        practice.practiceLocation
                                                                                    )}
                                                                                </Badge>
                                                                            ) : (
                                                                                "—"
                                                                            )}
                                                                        </Table.Td>


                                                                        <Table.Td>
                                                                            {practice.numberOfCourts ??
                                                                                "—"}
                                                                        </Table.Td>


                                                                        <Table.Td>
                                                                            {practice.numberOfPlayers ??
                                                                                "—"}
                                                                        </Table.Td>


                                                                        <Table.Td>
                                                                            {getPracticeMinutesText(
                                                                                practice.numberOfMinuts
                                                                            )}
                                                                        </Table.Td>


                                                                        <Table.Td>
                                                                            {practice.userId ??
                                                                                "—"}
                                                                        </Table.Td>


                                                                        <Table.Td>
                                                                            {practice.createdDate
                                                                                ? new Date(
                                                                                    practice.createdDate
                                                                                ).toLocaleString(
                                                                                    "da-DK"
                                                                                )
                                                                                : "—"}
                                                                        </Table.Td>

                                                                    </Table.Tr>
                                                                )
                                                            )}

                                                        </Table.Tbody>

                                                    </Table>
                                                </div>

                                            </Stack>
                                        </Paper>

                                    </>
                                )}

                            </Stack>
                        </Grid.Col>

                    </Grid>

                </Stack>
            </Container>
        </div>
    );
};

export default FormPracticeAdminPage;